/**
 * Template registry — the one list every surface reads.
 *
 * Registration is a STATIC IMPORT per template folder (no filesystem scanning:
 * bundlers can tree-shake and backend bundles stay deterministic). Adding a
 * template = create modules/reports/templates/<id>/ with an index.ts exporting
 * `reportTemplate`, add ONE import line here, and add its conf entry in
 * appwrite-functions/n8n-proxy/reports/template-conf.js. The parity test
 * (registry.test.ts) fails if a folder exists but is not registered.
 */
import { reportTemplate as leadSql } from './templates/lead-sql';
import { reportTemplate as leadMql } from './templates/lead-mql';
import { reportTemplate as campaignOverview } from './templates/campaign-overview';
import { reportTemplate as campaignCompanies } from './templates/campaign-companies';
import { reportTemplate as campaignLeads } from './templates/campaign-leads';
import { reportTemplate as campaignExport } from './templates/campaign-export';
import { reportTemplate as sellerCompany } from './templates/seller-company';
import type { Translations } from './schema/lead';

export type ReportLanguage = string;

export interface ReportTemplateConf {
  id: string;
  name: string;
  description: string;
  revision: string;
  /** Data sources this template renders — a template may combine several. */
  sourceKinds: readonly ('lead' | 'campaign' | 'seller')[];
  pdfBucket: 'lead-reports' | 'campaign-report-exports';
  /** Admin → Reports lists this template's configurable sections/sheets. */
  sectionConfig: boolean;
  /** 'dictionary' = free local translations first (fixed vocabulary); 'ai' = translate on demand. */
  i18n: 'ai' | 'dictionary';
  /**
   * 'document' (default): renders HTML and goes through report_publish (PDF).
   * 'export': a direct client-side download (XLSX/CSV) — no render, never
   * published or saved to a record.
   */
  kind?: 'document' | 'export';
}

interface DocumentTemplate<T> {
  validate: (value: unknown) => asserts value is T;
  translationTexts: (data: T) => Translations;
  validateTranslations: (data: T, translated: unknown) => asserts translated is Translations;
  render: (data: T, options: { language?: ReportLanguage; translations?: Translations }) => string;
}
/** Export templates download data; there is nothing to render or translate. */
interface ExportTemplate {
  validate: (value: unknown) => void;
}

export type ReportTemplateDef<T = unknown> =
  | ({ conf: ReportTemplateConf & { kind: 'document' | undefined } } & DocumentTemplate<T>)
  | ({ conf: ReportTemplateConf & { kind: 'export' } } & ExportTemplate);

type AnyTemplate = ({ conf: ReportTemplateConf } & Partial<DocumentTemplate<any>>) & { validate: (value: unknown) => void };

/** All registered templates. ONE import line per template folder. */
const REGISTERED: readonly AnyTemplate[] = [leadSql, leadMql, campaignOverview, campaignCompanies, campaignLeads, campaignExport, sellerCompany];

// Registry invariants — fail at load, in dev and in tests, never in production.
const ids = new Set<string>();
for (const t of REGISTERED) {
  if (!t?.conf?.id || !t.conf.name || !t.conf.revision) throw new Error(`Template registration incomplete: ${t?.conf?.id || '(unnamed)'}`);
  if (ids.has(t.conf.id)) throw new Error(`Duplicate template id: ${t.conf.id}`);
  if (!['lead-reports', 'campaign-report-exports'].includes(t.conf.pdfBucket)) throw new Error(`Template ${t.conf.id}: unknown pdfBucket ${t.conf.pdfBucket}`);
  if (!t.conf.sourceKinds.length) throw new Error(`Template ${t.conf.id}: sourceKinds is empty`);
  if (t.conf.kind !== 'export' && typeof (t as AnyTemplate).render !== 'function') throw new Error(`Template ${t.conf.id}: document templates must render`);
  if (t.conf.kind === 'export' && typeof (t as AnyTemplate).render === 'function') throw new Error(`Template ${t.conf.id}: export templates must not render`);
  ids.add(t.conf.id);
}

export const TEMPLATES: Record<string, AnyTemplate> = Object.fromEntries(
  REGISTERED.map(t => [t.conf.id, { ...t, id: t.conf.id }])
);

export const listTemplates = () => Object.values(TEMPLATES).map(({ conf }) => ({ id: conf.id, revision: conf.revision, name: conf.name, description: conf.description, kind: conf.kind ?? 'document' }));
export const getTemplate = (id: string): AnyTemplate | undefined => TEMPLATES[id];
/** Revision lookup by template id — any revision gate reads this. */
export const revisionOf = (id: string): string => TEMPLATES[id]?.conf.revision ?? '';
export type { SqlReport, Translations } from './schema/lead';
export type { CampaignReport } from './schema/campaign';
export { SQL_TEMPLATE, SQL_REVISION } from './schema/lead';
export { CAMPAIGN_TEMPLATE, CAMPAIGN_REVISION } from './schema/campaign';
